<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

import JsonUpload from '../components/JsonUpload.vue';
import CalculationDeleteButton from '../components/CalculationDeleteButton.vue';
import CalculationCopyButton from '../components/CalculationCopyButton.vue';

import { useSearchCalculations } from '../composables/useSearchCalculations';
import { useCalculationFileHandler } from '../composables/useCalculationFileHandler';
import { useUserStore } from '../store/user';

import type { Calculation } from '../utils/types';

const route = useRoute();

const { search, searchedCalculations } = useSearchCalculations(Number(route.params.calculatorId));

const { isLoading, addCalculationFromFile } = useCalculationFileHandler();

const userStore = useUserStore();

const canCopy = computed(() => {
  const accountType = userStore.user?.account_type;

  return accountType === 'admin' || accountType === 'engineer' || accountType === 'seller';
});

const expandedRows = ref<Record<string, boolean>>({});

function toggleExpansion(data: Calculation) {
  if (data.id === null) return;

  const key = String(data.id);

  if (expandedRows.value[key]) {
    const { [key]: _removed, ...rest } = expandedRows.value;
    expandedRows.value = rest;
  } else {
    expandedRows.value = { ...expandedRows.value, [key]: true };
  }
}

function ownerName(owner: Calculation['owner']): string {
  if (!owner) return '—';

  return `${owner.first_name} ${owner.last_name}`.trim() || '—';
}
</script>

<template>
  <div class="space-y-6 py-9">
    <div class="flex items-center gap-4">
      <h1 class="mr-auto font-lora text-3xl text-gray-900">Lista moich kalkulacji</h1>

      <IconField class="w-[430px]">
        <InputIcon class="pi pi-search" />
        <InputText class="w-[430px]" v-model="search" placeholder="Szukaj" />
      </IconField>

      <JsonUpload
        chooseLabel="Dodaj z pliku"
        :isLoading="isLoading"
        @update="addCalculationFromFile"
      />
    </div>

    <DataTable
      class="overflow-hidden rounded-xl border border-gray-200 [&__.p-row-odd]:bg-gray-50"
      :value="searchedCalculations"
      v-model:expandedRows="expandedRows"
      dataKey="id"
      :rowClass="(data: Calculation) => [{ '!bg-red-100': data.outdated || data.id === null }]"
      removableSort
      tableStyle="min-width: 50rem"
    >
      <Column field="id" header="Id" sortable style="width: 5%"></Column>

      <Column field="name" header="Nazwa" sortable style="width: 30%"></Column>

      <Column header="Klient" style="width: 25%">
        <template #body="{ data }: { data: Calculation & { clientName: string } }">
          {{ data.clientName }}
        </template>
      </Column>

      <Column header="Kopie" style="width: 8%">
        <template #body="{ data }: { data: Calculation }">
          <button
            v-if="data.calculation_children && data.calculation_children.length > 0"
            type="button"
            class="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
            @click="toggleExpansion(data)"
          >
            <span>{{ data.calculation_children.length }}</span>
            <i
              :class="[
                'pi text-xs',
                data.id !== null && expandedRows[String(data.id)]
                  ? 'pi-chevron-up'
                  : 'pi-chevron-down',
              ]"
            ></i>
          </button>
          <span v-else class="text-sm text-gray-400">0</span>
        </template>
      </Column>

      <Column header="Stworzył" style="width: 15%">
        <template #body="{ data }: { data: Calculation }">
          {{ ownerName(data.owner) }}
        </template>
      </Column>

      <Column header="" style="width: 17%">
        <template #body="{ data }: { data: Calculation }">
          <div class="flex justify-end gap-4">
            <CalculationCopyButton
              v-if="canCopy && data.id"
              :calculationId="data.id"
            />

            <RouterLink
              :to="`/${$route.params.calculatorId}/client/${data.client_uuid}/calculation/${data.uuid}`"
            >
              <Button class="!p-2" size="small" severity="secondary" outlined>
                <i class="pi pi-pen-to-square"></i>
              </Button>
            </RouterLink>

            <CalculationDeleteButton v-if="data.id" :calculationId="data.id" />
          </div>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="space-y-2 bg-gray-50 px-6 py-4">
          <div
            v-for="child in slotProps.data.calculation_children"
            :key="child.id"
            class="flex items-center gap-4 rounded-md border border-gray-200 bg-white px-4 py-2"
          >
            <span class="w-16 text-sm text-gray-500">{{ child.id }}</span>
            <span class="flex-1 text-sm">{{ child.name }}</span>
            <span class="w-40 text-sm text-gray-700">{{ ownerName(child.owner) }}</span>

            <RouterLink
              :to="`/${$route.params.calculatorId}/client/${child.client_uuid}/calculation/${child.uuid}`"
            >
              <Button class="!p-2" size="small" severity="secondary" outlined>
                <i class="pi pi-pen-to-square"></i>
              </Button>
            </RouterLink>

            <CalculationDeleteButton v-if="child.id" :calculationId="child.id" />
          </div>
        </div>
      </template>
    </DataTable>
  </div>
</template>
